// src/hooks/useLeaveConfirmation.ts
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

export function useLeaveConfirmation(shouldBlock: boolean) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);
  const navigate = useNavigate();

  // Expose this so your manual custom buttons (like 'Back') can use the same logic
  const attemptExit = useCallback(
    (action: () => void) => {
      if (shouldBlock) {
        setPendingAction(() => action);
        setIsModalOpen(true);
      } else {
        action();
      }
    },
    [shouldBlock],
  );

  const confirmExit = useCallback(() => {
    setIsModalOpen(false);
    if (pendingAction) pendingAction();
  }, [pendingAction]);

  const cancelExit = useCallback(() => {
    setIsModalOpen(false);
    setPendingAction(null);
  }, []);

  useEffect(() => {
    if (!shouldBlock) return;

    // 1. Intercept Tab Close / Page Refresh natively
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ""; // Required by Chrome to show the native warning prompt
    };

    // 2. Intercept ANY internal anchor tags (like sidebar links) before React Router does
    const handleLinkClick = (e: MouseEvent) => {
      if (
        e.defaultPrevented ||
        e.button !== 0 ||
        e.metaKey ||
        e.ctrlKey ||
        e.shiftKey ||
        e.altKey
      ) {
        return;
      }
      // Find the closest anchor tag that was clicked
      const target = (e.target as HTMLElement).closest("a");
      if (!target || !target.href) return;

      // Check if it's pointing to our own app, but a different route
      const isInternal = target.origin === window.location.origin;
      const isDifferentPath =
        target.pathname + target.search !== window.location.pathname + window.location.search;

      if (isInternal && isDifferentPath) {
        e.preventDefault(); // Stop native browser navigation
        e.stopPropagation(); // Stop React Router from seeing the click

        const nextPath = target.pathname + target.search;

        setPendingAction(() => () => navigate(nextPath));
        setIsModalOpen(true);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    // 'capture: true' is the magic trick here. It intercepts the event on its way DOWN the DOM tree,
    // beating React Router's synthetic events to the punch.
    document.addEventListener("click", handleLinkClick, { capture: true });

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("click", handleLinkClick, { capture: true });
    };
  }, [shouldBlock, navigate]);

  return {
    isBlocked: isModalOpen,
    attemptExit,
    confirmExit,
    cancelExit,
  };
}
